// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, PinInput } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Card } from '@/components/ui/data-display';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { IconArrowLeft, IconDeviceMobile, IconSparkles, IconCheck } from '@tabler/icons-react';
import { FadeIn, SlideIn, ScaleIn, HoverLift, useReducedMotion } from '@/components/animations';

export default function OtpAnimated() {
  const [otp, setOtp] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const reduceMotion = useReducedMotion();

  const handleChange = (value: string) => {
    setOtp(value);
    setIsComplete(value.length === 6);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerified(true);
  };

  if (isVerified) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: isDark
            ? `linear-gradient(135deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
            : `linear-gradient(135deg, ${theme.colors.gray[0]} 0%, ${theme.colors.green[0]} 100%)`,
        }}
      >
        <Center mih="100vh" p="md">
          <Container size="xs" w="100%">
            <ScaleIn delay={100}>
              <Card variant="elevated" p="xl" radius="lg">
                <Stack gap="lg" align="center">
                  <ScaleIn delay={200}>
                    <Center
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 20,
                        backgroundColor: 'var(--mantine-color-green-light)',
                      }}
                    >
                      <IconCheck size={40} color="var(--mantine-color-green-6)" />
                    </Center>
                  </ScaleIn>

                  <FadeIn delay={300}>
                    <Stack align="center" gap="xs">
                      <Title order={2} ta="center">Verified!</Title>
                      <Text c="dimmed" ta="center" maw={300}>
                        Your identity has been successfully verified.
                      </Text>
                    </Stack>
                  </FadeIn>

                  <SlideIn direction="up" delay={400}>
                    <Button
                      component={Link}
                      to="/app/demo/auth-pages/login/animated"
                      fullWidth
                      size="md"
                      radius="md"
                      variant="gradient"
                      gradient={{ from: 'green', to: 'teal' }}
                    >
                      Continue
                    </Button>
                  </SlideIn>
                </Stack>
              </Card>
            </ScaleIn>
          </Container>
        </Center>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark
          ? `linear-gradient(135deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
          : `linear-gradient(135deg, ${theme.colors.gray[0]} 0%, ${theme.colors.violet[0]} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {[...Array(4)].map((_, i) => (
          <Box
            key={i}
            style={{
              position: 'absolute',
              width: 80 + i * 40,
              height: 80 + i * 40,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.colors.violet[isDark ? 8 : 3]}, ${theme.colors.indigo[isDark ? 8 : 3]})`,
              opacity: 0.1,
              top: `${10 + i * 20}%`,
              left: `${5 + i * 20}%`,
              animation: reduceMotion ? 'none' : `float ${4 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </Box>

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <ScaleIn delay={100} duration={500}>
            <HoverLift>
              <Card variant="elevated" p="xl" radius="lg">
                <Stack gap="lg">
                  <SlideIn direction="left" delay={200}>
                    <Button
                      component={Link}
                      to="/app/demo/auth-pages/otp"
                      variant="subtle"
                      size="xs"
                      leftSection={<IconArrowLeft size={14} />}
                      w="fit-content"
                    >
                      Back to variants
                    </Button>
                  </SlideIn>

                  <FadeIn delay={300}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconSparkles size={12} />}
                        variant="gradient"
                        gradient={{ from: 'violet', to: 'indigo' }}
                        size="lg"
                      >
                        Animated Experience
                      </Badge>
                      <Center
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 14,
                          backgroundColor: 'var(--mantine-color-violet-light)',
                          marginTop: 'var(--mantine-spacing-sm)',
                        }}
                      >
                        <IconDeviceMobile size={28} color="var(--mantine-color-violet-6)" />
                      </Center>
                      <Title order={2} ta="center" mt="sm">
                        Verification code
                      </Title>
                      <Text c="dimmed" size="sm" ta="center" maw={300}>
                        We've sent a 6-digit code to your phone ending in **42
                      </Text>
                    </Stack>
                  </FadeIn>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="lg">
                      <SlideIn direction="up" delay={400}>
                        <Center>
                          <PinInput
                            length={6}
                            size="lg"
                            value={otp}
                            onChange={handleChange}
                            type="number"
                            oneTimeCode
                            aria-label="Verification code"
                          />
                        </Center>
                      </SlideIn>

                      <FadeIn delay={500}>
                        <Button
                          type="submit"
                          fullWidth
                          size="md"
                          radius="md"
                          variant="gradient"
                          gradient={{ from: 'violet', to: 'indigo' }}
                          disabled={!isComplete}
                        >
                          Verify code
                        </Button>
                      </FadeIn>
                    </Stack>
                  </form>

                  <FadeIn delay={600}>
                    <Text ta="center" size="sm" c="dimmed">
                      Didn't receive the code?{' '}
                      <Anchor component="button" type="button" fw={500}>
                        Resend
                      </Anchor>
                    </Text>
                  </FadeIn>

                  <FadeIn delay={700}>
                    <Center>
                      <Anchor component={Link} to="/app/demo/auth-pages/login/animated" size="sm">
                        <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        Back to login
                      </Anchor>
                    </Center>
                  </FadeIn>
                </Stack>
              </Card>
            </HoverLift>
          </ScaleIn>
        </Container>
      </Center>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </Box>
  );
}
